import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import useNavigate from "src/hooks/useNavigate";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { TrialInstitutionMonitoringIssuesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import * as MonitoringIssueActions from "../../../actions/monitoringIssue";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { InlineLoading } from "src/components/common";
import { useParams } from "react-router";
import { MonitoringSubMenu } from "../../../helpers/subMenuHelper";

import "./MonitoringIssuesPage.scss";
import { findLookup } from "src/helpers/lookupHelper";

interface MonitoringIssuesPageProps {
    monitoringIssues: Dtos.MonitoringIssue[];
    loadingMonitoringIssues: boolean;
    loadMonitoringIssuesSuccess: boolean;
    loadMonitoringIssuesFailure: boolean;
    loadMonitoringIssuesErrorCode: string;
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    lookups: Dtos.Lookup[];
    clearMonitoringIssues: MonitoringIssueActions.IMonitoringIssueClearActionCreator;
    loadMonitoringIssues: MonitoringIssueActions.IMonitoringIssuesLoadByMonitoringIdActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const MonitoringIssuesPage: FC<MonitoringIssuesPageProps> = ({
    monitoringIssues,
    loadingMonitoringIssues,
    loadMonitoringIssuesSuccess,
    loadMonitoringIssuesFailure,
    loadMonitoringIssuesErrorCode,
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    pageSize,
    lookups,
    clearMonitoringIssues,
    loadMonitoringIssues,
    clearTrialInstitution,
    loadTrialInstitution,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10); 
    const monitoringIdNumber = parseInt(monitoringId, 10); 
    
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showMonitoringIssues = 
        !loadingMonitoringIssues && 
        loadMonitoringIssuesSuccess &&
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess;

    // Load Monitoring Issues
    useEffect(() => {
        if (!initalised) {
            setBreadcrumbs(
                TrialInstitutionMonitoringIssuesBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber
                )
            );

            loadMonitoringIssues(monitoringIdNumber);

            setInitalised(true);
        }
    }, [
        initalised,
        trialIdNumber,
        trialInstitutionIdNumber,
        monitoringIdNumber
    ]);

    // Load Trial Institution
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution
    ]);

    // Update drawer items & breadcrumbs
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(MonitoringSubMenu(trialIdNumber, trialInstitutionIdNumber, monitoringIdNumber, permissions));
            setBreadcrumbs(
                TrialInstitutionMonitoringIssuesBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber, 
        trialInstitutionIdNumber, 
        monitoringIdNumber,
        trialInstitution, 
        permissions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringIssuesFailure) {
            navigate(`/error/${loadMonitoringIssuesErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`)
        }
    }, [
        loadMonitoringIssuesFailure, 
        loadMonitoringIssuesErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Cleanup effect to clear the monitoring issues & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringIssues();
            clearTrialInstitution();
        };
    }, [clearMonitoringIssues, clearTrialInstitution]);

    const renderTable = useMemo(() => {
        if (monitoringIssues?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no major deviations to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = monitoringIssues?.length > pageSize;
        
        return (
            <div className="table-wrapper">
                <ReactTable
                    data={monitoringIssues}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : monitoringIssues?.length}
                    pageSize={showPagination ? pageSize : monitoringIssues?.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no major deviations to view"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "dateEntered",
                            desc: true,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.MONITORINGISSUE} fixedWidth />
                            ),
                        },
                        {
                            id: "monitoringIssueTypeId",
                            Header: "Issue Type",
                            accessor: "monitoringIssueTypeId",
                            Cell: (props) => (
                                <span>
                                    {findLookup("MonitoringIssue", "monitoringIssueTypeId", lookups)?.values?.find(x => x.id == props.value)?.value}
                                </span>
                            ),
                        },
                        {
                            id: "monitoringIssueDeviationType",
                            Header: "Major Deviation Type",
                            accessor: "monitoringIssueDeviationTypeId",
                            Cell: (props) => (
                                <span>
                                    {findLookup("MonitoringIssue", "monitoringIssueDeviationTypeId", lookups)?.values?.find(x => x.id == props.value)?.value}
                                </span>
                            ),
                        },
                        {
                            id: "monitoringIssueCategoryId",
                            Header: "Category",
                            accessor: "monitoringIssueCategoryId",
                            Cell: (props) => (
                                <span>
                                    {findLookup("MonitoringIssue", "monitoringIssueCategoryId", lookups)?.values?.find(x => x.id == props.value)?.value}
                                </span>
                            ),
                        },
                        {
                            id: "monitoringIssueStatusId",
                            Header: "Status",
                            accessor: "monitoringIssueStatusId",
                            Cell: (props) => (
                                <span>
                                    {findLookup("MonitoringIssue", "monitoringIssueStatusId", lookups)?.values?.find(x => x.id == props.value)?.value}
                                </span>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        monitoringIssues,
        pageSize,
        PAGE_SIZE_OPTIONS,
        setPageSize,
        lookups
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Issues</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.MonitoringIssueCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showMonitoringIssues ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showMonitoringIssues ? (
                    <div className="card">
                        <div className="patient-tables">{renderTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showMonitoringIssues,
        renderTable,
        trialIdNumber,
        trialInstitutionIdNumber,
        lookups
    ]);

    return (
        <RestrictedLayout
            loading={loadingMonitoringIssues || loadingTrialInstitution}
            permission={Dtos.Permission.MonitoringIssueView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringIssuesPageProps> => {
    let monitoringIssues: Dtos.MonitoringIssue[] | undefined =
        state.monitoringIssue.formData instanceof Array
            ? state.monitoringIssue.formData
            : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined =
        !(state.trialInstitution.formData instanceof Array)
            ? state.trialInstitution.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        monitoringIssues: monitoringIssues!,
        loadingMonitoringIssues:
            state.monitoringIssue.loadState &&
            state.monitoringIssue.loadState.status == RequestState.Pending,
        loadMonitoringIssuesSuccess:
            state.monitoringIssue.loadState &&
            state.monitoringIssue.loadState.status == RequestState.Success,
        loadMonitoringIssuesFailure:
            state.monitoringIssue.loadState &&
            state.monitoringIssue.loadState.status == RequestState.Failure,
        loadMonitoringIssuesErrorCode: state.monitoringIssue.loadState.errorCode,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        lookups: state.monitoringIssue.lookups,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringIssuesPageProps> => {
    return {
        loadMonitoringIssues: bindActionCreators(MonitoringIssueActions.LoadMonitoringIssuesByMonitoringId, dispatch),
        clearMonitoringIssues: bindActionCreators(MonitoringIssueActions.Clear, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringIssuesPage);
